"use client";

import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check } from 'lucide-react';

const Pricing = () => {
  const navigate = useNavigate();
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('month');

  const basePrices = {
    basic: 500,
    pro: 7900,
    enterprise: 39900,
  };

  const yearlyPrices = {
    basic: Math.round(basePrices.basic * 12 * 0.9),
    pro: Math.round(basePrices.pro * 12 * 0.9),
    enterprise: Math.round(basePrices.enterprise * 12 * 0.9),
  };

  const plans = [
    {
      name: "Basic",
      monthlyPrice: "£5 / month",
      yearlyPrice: "£54 / year (Save 10%)",
      description: "Perfect for getting started",
      features: [
        "Up to 5 projects",
        "Up to 2 agents (team members)",
        "Up to 5 clients",
        "Basic time tracking",
        "Email support"
      ],
      cta: "Start for £5/mo",
      popular: false,
      upgradeTo: 'pro' as 'pro' | 'enterprise',
    },
    {
      name: "Pro",
      monthlyPrice: "£79 / month",
      yearlyPrice: "£853 / year (Save 10%)",
      description: "Best for growing teams",
      features: [
        "Up to 20 projects",
        "Up to 50 agents (team members)",
        "Up to 500 clients",
        "Advanced analytics & reports",
        "Client portals with branding",
        "Priority email support",
        "Custom themes & integrations"
      ],
      cta: "Most Popular",
      popular: true,
      upgradeTo: 'pro' as 'pro' | 'enterprise',
    },
    {
      name: "Enterprise",
      monthlyPrice: "£399 / month",
      yearlyPrice: "£4,309 / year (Save 10%)",
      description: "Unlimited scale for your business",
      features: [
        "Unlimited projects, agents, & clients",
        "Everything in Pro",
        "Dedicated account manager",
        "Custom integrations (API/Slack)",
        "99.9% uptime SLA",
        "Advanced security & compliance"
      ],
      cta: "Contact Sales",
      popular: false,
      upgradeTo: 'enterprise' as 'pro' | 'enterprise',
    },
  ];

  const handleUpgrade = (planToUpgrade: 'pro' | 'enterprise') => {
    const amount = billingInterval === 'year' ? yearlyPrices[planToUpgrade] : basePrices[planToUpgrade];
    // For public pricing, redirect to signup with plan and interval as query params
    // (Onboarding will handle creating company and session)
    navigate(`/signup?plan=${planToUpgrade}&interval=${billingInterval}&amount=${amount}`);
  };

  const getCtaText = (plan: typeof plans[0]) => {
    const intervalText = billingInterval === 'year' ? 'Yearly' : 'Monthly';
    return `${plan.name} Plan (${intervalText})`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-green-50 to-blue-50 relative overflow-hidden">
      {/* Subtle Geometric Patterns */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zMCAzMCBMMzAgMzAgWiIgZmlsbD0iI2UwZjJmMSIgb3BhY2l0eT0iMC4xIi8+PC9nPjwvc3ZnPg==')]"></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-green-500/5 rounded-full blur-3xl animate-pulse"></div>
      </div>

      <div className="container max-w-6xl mx-auto px-4 py-20 relative z-10">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 text-foreground">
            Simple, Fixed Pricing (GBP)
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            No per-user fees. Pay once, scale within limits. Upgrade to Enterprise for unlimited agents, clients, and projects. All prices in GBP (£). 7-day money back guarantee on all plans.
          </p>
          {/* Custom Segmented Toggle: Two-button tab-like design */}
          <div className="flex justify-center items-center mb-8">
            <div className="flex bg-muted rounded-full border border-border shadow-sm overflow-hidden">
              <button
                onClick={() => setBillingInterval('month')}
                className={`
                  px-6 py-3 text-sm font-semibold transition-all duration-300 ease-in-out flex items-center space-x-2
                  ${billingInterval === 'month'
                    ? 'bg-primary text-primary-foreground shadow-md' 
                    : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                  }
                `}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingInterval('year')}
                className={`
                  px-6 py-3 text-sm font-semibold transition-all duration-300 ease-in-out flex items-center space-x-2
                  ${billingInterval === 'year'
                    ? 'bg-primary text-primary-foreground shadow-md' 
                    : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                  }
                `}
              >
                Yearly (Save 10%)
              </button>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <Card key={plan.name} className={plan.popular ? "ring-2 ring-primary shadow-lg" : ""}>
              <CardHeader>
                <CardTitle className="text-2xl font-bold">{plan.name}</CardTitle>
                <div className="text-4xl font-bold text-primary">
                  {billingInterval === 'year' ? plan.yearlyPrice : plan.monthlyPrice}
                </div>
                {plan.popular && <Badge variant="secondary" className="mt-2">Most Popular</Badge>}
                <CardDescription className="mt-4">{plan.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-2">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center space-x-2">
                      <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  variant={plan.popular ? "default" : "outline"}
                  onClick={() => plan.upgradeTo && handleUpgrade(plan.upgradeTo)}
                  className="w-full px-6 py-3 text-sm" // Added padding and text-sm for better fit
                  disabled={!plan.upgradeTo}
                >
                  {getCtaText(plan)}
                </Button>
                {plan.upgradeTo === null && (
                  <p className="text-xs text-muted-foreground text-center">Already on Basic? <Link to="/dashboard/billing" className="text-primary hover:underline">Upgrade now</Link></p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-16">
          <p className="text-lg text-muted-foreground mb-4">All plans come with a 7-day money back guarantee. Questions? <Link to="/contact" className="text-primary hover:underline">Contact sales</Link> for custom Enterprise plans.</p>
          <p className="text-sm text-muted-foreground">Prices in GBP (£). Cancel anytime within 7 days for a full refund. Basic plans use subdomains; Pro and Enterprise support custom domains.</p>
        </div>
      </div>
    </div>
  );
};

export default Pricing;